import { useState } from 'react';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE CONSENTEMENT DÛ PAR LE BLOC TIERS — S-24 DU BRIEF, ET IL NE VIT QUE SUR SA
 * PAGE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **CETTE FEATURE N'APPELLE AUCUNE ROUTE, ET C'EST LE POINT.** F-72 : « **AUCUNE DONNÉE
 * N'ENTRE DANS LE PRODUIT.** Ni en base, ni en fichier, ni en email reçu. Une demande de
 * contact **n'est pas un objet du produit** : rien à ranger, rien à lire, rien à compter,
 * **aucun droit dessus, aucune ligne de trace, aucune durée de conservation à déclarer**. »
 * Et « **aucun email ne part du produit** à l'occasion d'une demande de contact, et **le
 * produit n'apprend même pas qu'une demande est arrivée** ».
 *
 * Une feature sans hook de données est inhabituelle sur ce projet ; c'est **exactement la
 * forme que la règle impose**. Ce qu'elle porte est un état qui ne quitte jamais le
 * navigateur du visiteur.
 *
 * **Les deux adresses — le formulaire et l'email — vivent dans `shared/contact-du-groupe.ts`**
 * et non ici : le pied de page les affiche, et `shared/` ne remonte jamais vers une feature.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **POURQUOI UN CONSENTEMENT ICI, ET SEULEMENT ICI.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-72 : « **un bandeau de consentement n'est dû que par le bloc tiers, sur sa page, et par
 * rien d'autre de cette surface.** » Le brief le confirme : S-24 « vit sur S-18 ».
 *
 * **Le motif est la zone floue 8 du corpus, la seule de `CDC-6`, et son repli est écrit** :
 * ce qui reste ouvert est « **ce que le bloc tiers dépose sur l'appareil de la personne** —
 * un identifiant, un compteur, rien — et **à quel moment il se charge** », son ancrage étant
 * « **aucun : le code embarqué n'existe pas encore** ». Repli au lancement : « **le bloc est
 * traité COMME S'IL DÉPOSAIT UN IDENTIFIANT** : la page qui le porte est **la seule** à en
 * porter la conséquence, et tout consentement dû se limite à cette page. »
 *
 * **CE QUE CE REPLI IMPOSE AU CODE EST UN ORDRE, ET C'EST PLUS FORT QU'UN BANDEAU** : le
 * cadre n'est **pas monté** avant le consentement. Un bandeau posé au-dessus d'un cadre déjà
 * chargé demande la permission **après** l'avoir prise — c'est le défaut le plus répandu de
 * ce genre de bandeau, et **il ne se voit pas à l'écran**.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LE CHOIX N'EST PAS CONSERVÉ, ET C'EST LA DÉCISION LA PLUS DISCUTABLE DE CE FICHIER
 * — DONC ELLE S'ÉCRIT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Le consentement vit dans l'état du composant, **pas dans le stockage du navigateur**. Il se
 * redemande donc à chaque visite de la page.
 *
 * **Ce que cela coûte** : une personne qui revient sur la page de contact reclique une fois.
 *
 * **Ce que cela achète, et c'est pourquoi ce sens a été retenu** : le produit ne dépose
 * **rien** sur l'appareil de qui n'a rien accepté. Or F-73 est catégorique sur cette
 * surface — « **AUCUNE MESURE D'AUDIENCE**, aucun compteur de visites, **aucun identifiant
 * déposé à cette fin** » —, et le passage de quelqu'un « ne laisse **RIEN** dans le
 * produit », cinquième dérogation au régime des traces et « **la première qui porte sur une
 * SURFACE ENTIÈRE** ». Écrire un marqueur de consentement serait **la seule chose que ce site
 * dépose chez ses visiteurs**, sur la seule page où le corpus se méfie déjà d'un dépôt.
 *
 * **CLAUSE DE RÉOUVERTURE, ÉCRITE POUR QUE LA QUESTION SE REPOSE AVEC SA RÉPONSE.** Le jour
 * où le code embarqué est obtenu et où l'on sait ce qu'il dépose, **la zone floue 8 se
 * ferme** : c'est ce jour-là qu'un marqueur persistant se décide — avec sa durée, ce qui est
 * une décision de conservation et non un réglage d'affichage.
 */
export function useConsentementAuBlocTiers(): {
  readonly accorde: boolean;
  readonly accorder: () => void;
} {
  const [accorde, poser] = useState(false);
  return { accorde, accorder: () => poser(true) };
}
